'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Elements } from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import CheckoutForm from './components/CheckoutForm';
import OrderSummary from './components/OrderSummary';

const stripePromise = getStripe();

export default function CheckoutPage() {
  const router = useRouter();
  const productPrice = 9.99;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Create payment intent when component mounts
    const createPaymentIntent = async () => {
      try {
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: productPrice,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to initialize payment');
        }

        setClientSecret(data.clientSecret);
      } catch (err) {
        console.error('Payment initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize payment');
      }
    };

    createPaymentIntent();
  }, [productPrice]);

  const handlePaymentSuccess = (paymentIntent: PaymentIntent) => {
    // Redirect to success page with payment intent ID
    router.push(`/success?payment_intent=${paymentIntent.id}`);
  };

  if (error) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-32 pb-20 px-6 md:px-12 bg-background">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-error/10 border border-error rounded-xl p-8">
              <h2 className="text-2xl font-bold text-foreground mb-4">Payment Setup Error</h2>
              <p className="text-error mb-6">{error}</p>
              <button
                onClick={() => router.push('/homepage')}
                className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-6 rounded-xl transition-all"
              >
                Return to Homepage
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen pt-32 pb-20 px-6 md:px-12 bg-background">
        <div className="max-w-[1400px] mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12 reveal">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Complete Your Purchase
            </h1>
            <p className="text-lg text-muted-foreground">
              You're one step away from transforming your job search
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column - Checkout Form */}
            <div className="lg:col-span-7 reveal">
              {clientSecret ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#2563eb',
                        colorBackground: '#ffffff',
                        colorText: '#1e293b',
                        colorDanger: '#ef4444',
                        fontFamily: 'system-ui, sans-serif',
                        borderRadius: '12px',
                      },
                    },
                  }}
                >
                  <CheckoutForm
                    clientSecret={clientSecret}
                    onPaymentSuccess={handlePaymentSuccess}
                  />
                </Elements>
              ) : (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-foreground">Preparing checkout...</p>
                </div>
              )}
            </div>

            {/* Right Column - Order Summary (Sticky) */}
            <div className="lg:col-span-5 reveal" style={{ transitionDelay: '200ms' }}>
              <OrderSummary price={productPrice} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}